// ✅ src/pages/TarMixing/ProductionOutwardListing.jsx
// Updated with safe localStorage handling + common headers

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaPlus, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v, def = "") => {
  const s = String(v ?? "").trim();
  return s || def;
};

const fmtDate = (value) => {
  if (!value) return "";

  const s = String(value).trim();

  // already dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const ProductionOutwardListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ Safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchid = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchtype = useMemo(
    () => toStr(branchTypeLS, "HO"),
    [branchTypeLS]
  );
  const userid = useMemo(() => toStr(userIdLS, ""), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchtype,
      "x-branchtype": branchtype,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  // Toggle state for filters visibility
  const [showFilters, setShowFilters] = useState(true);

  // Dropdown data
  const [projectOptions, setProjectOptions] = useState([]);
  const [productionOptions, setProductionOptions] = useState([]);

  // Listing data
  const [outwardData, setOutwardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingProductions, setLoadingProductions] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    fromDate: "12/08/2025",
    toDate: "11/09/2025",
    projectid: "",
    productionid: "All",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ----------------------------
  // ✅ API Calls
  // ----------------------------
  const loadProjects = async () => {
    try {
      setLoadingProjects(true);

      const res = await axios.get(`${BASE_URL}/api/outwardListing/projects`, {
        params: { branchid, userid, branchtype },
        headers: commonHeaders,
      });

      setProjectOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load projects");
      setProjectOptions([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadProductionsByProject = async (projectid) => {
    try {
      if (!projectid) {
        setProductionOptions([]);
        return;
      }

      setLoadingProductions(true);

      const res = await axios.get(`${BASE_URL}/api/outwardListing/productions`, {
        params: { branchid, userid, branchtype, projectid },
        headers: commonHeaders,
      });

      setProductionOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load productions");
      setProductionOptions([]);
    } finally {
      setLoadingProductions(false);
    }
  };

  const loadListing = async () => {
    try {
      setLoading(true);

      const params = {
        branchid,
        userid,
        branchtype,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        projectid: formData.projectid || null,
        productionid:
          formData.productionid === "All" ? null : formData.productionid,
      };

      const res = await axios.get(`${BASE_URL}/api/outwardListing/list`, {
        params,
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      setOutwardData(data);

      if (data.length === 0) {
        toast.info("No Records found");
      } else {
        toast.success(`Found ${data.length} record(s)`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load listing");
      setOutwardData([]);
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------
  // ✅ Initial Load
  // ----------------------------
  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchid, userid, branchtype, commonHeaders]);

  // ✅ When project changes -> load productions
  useEffect(() => {
    setFormData((prev) => ({ ...prev, productionid: "All" }));
    loadProductionsByProject(formData.projectid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectid]);

  // ----------------------------
  // UI handlers
  // ----------------------------
  const handleSearch = () => {
    if (!formData.fromDate || !formData.toDate) {
      toast.warning("Please select From Date and To Date");
      return;
    }
    loadListing();
  };

  const handleAddNew = () => {
    navigate("/TarMixing/Tar_Outward_Entry");
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION OUTWARD LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production Outward Listing
            </h1>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left */}
                <div className="space-y-4">
                  <div>
                    <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      From Date
                    </label>
                    <input
                      type="text"
                      value={formData.fromDate}
                      onChange={(e) => handleInputChange("fromDate", e.target.value)}
                      placeholder="DD/MM/YYYY"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Project Name
                    </label>
                    <select
                      value={formData.projectid}
                      onChange={(e) => handleInputChange("projectid", e.target.value)}
                      disabled={loadingProjects}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">
                        {loadingProjects ? "Loading..." : "Select"}
                      </option>
                      {projectOptions.map((p, idx) => (
                        <option
                          key={p.id ?? p.projectid ?? p.ProjectID ?? idx}
                          value={p.id ?? p.projectid ?? p.ProjectID ?? ""}
                        >
                          {p.name ?? p.projectName ?? p.ProjectName ?? ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right */}
                <div className="space-y-4">
                  <div>
                    <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      To Date
                    </label>
                    <input
                      type="text"
                      value={formData.toDate}
                      onChange={(e) => handleInputChange("toDate", e.target.value)}
                      placeholder="DD/MM/YYYY"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Production Name
                    </label>
                    <select
                      value={formData.productionid}
                      onChange={(e) => handleInputChange("productionid", e.target.value)}
                      disabled={!formData.projectid || loadingProductions}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="All">
                        {loadingProductions ? "Loading..." : "All"}
                      </option>
                      {productionOptions.map((pr, idx) => (
                        <option
                          key={pr.productionId ?? pr.id ?? idx}
                          value={pr.productionId ?? pr.id ?? ""}
                        >
                          {pr.productionName ?? pr.name ?? ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="flex justify-center items-center mt-6 gap-4 p-2">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          )}

          {/* Data */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {outwardData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No Records found
                </div>
              </div>
            ) : (
              <div className={`overflow-x-auto ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <table className="min-w-full text-sm">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-left">S.No</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Outward Code</th>
                      <th className="px-4 py-3 text-left">Project</th>
                      <th className="px-4 py-3 text-left">Production</th>
                      <th className="px-4 py-3 text-right">Available Qty</th>
                      <th className="px-4 py-3 text-right">Transferred Qty</th>
                      <th className="px-4 py-3 text-right">Balance Qty</th>
                      <th className="px-4 py-3 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outwardData.map((row, idx) => (
                      <tr
                        key={row.fld_ProdOutID || idx}
                        className={`border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}
                      >
                        <td className="px-4 py-3">{idx + 1}</td>
                        <td className="px-4 py-3">{fmtDate(row.fld_Date)}</td>
                        <td className="px-4 py-3">{row.fld_outwardcode}</td>
                        <td className="px-4 py-3">{row.fld_ProjectName}</td>
                        <td className="px-4 py-3">{row.fld_ProductionName}</td>
                        <td className="px-4 py-3 text-right">{row.AvailableQty}</td>
                        <td className="px-4 py-3 text-right">{row.TransferedQty}</td>
                        <td className="px-4 py-3 text-right">{row.BalanceQty}</td>
                        <td className="px-4 py-3">
                          {row.fld_isapproved ? "Approved" : "Pending"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionOutwardListing;